import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { authService, type AuthMeProfile } from '@/api/services/auth';
import type { MeProfile } from '@/api/services/me';

type MeContextValue = {
  me: MeProfile | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const MeContext = createContext<MeContextValue | null>(null);

function mapAuthMeProfile(profile: AuthMeProfile): MeProfile {
  return {
    id: profile.id,
    role: profile.role,
    plan: profile.plan,
    status: profile.status,
    organizationId: profile.organizationId,
    activeWorkspaceMode: profile.activeWorkspaceMode,
    activeWorkspaceName: profile.activeWorkspaceName,
    workspaceCount: profile.workspaceCount,
    billingBypass: profile.billingBypass,
    email: profile.email,
    name: profile.name,
    membershipRole: profile.membershipRole,
    currentWorkspace: profile.currentWorkspace,
    currentWorkspacePlan: profile.currentWorkspacePlan,
    permissions: profile.permissions,
    termsAcceptanceRequired: profile.termsAcceptanceRequired,
    currentTermsVersion: profile.currentTermsVersion,
    acceptedTermsVersion: profile.acceptedTermsVersion,
  };
}

export function MeProvider({
  children,
  initialProfile,
}: {
  children: React.ReactNode;
  initialProfile?: AuthMeProfile | null;
}) {
  const [me, setMe] = useState<MeProfile | null>(
    initialProfile ? mapAuthMeProfile(initialProfile) : null,
  );
  const [loading, setLoading] = useState(initialProfile == null);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const profile = await authService.me();
      setMe(mapAuthMeProfile(profile));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialProfile == null) {
      void refresh();
    }
  }, [initialProfile]);

  const value = useMemo(
    () => ({ me, loading, error, refresh }),
    [me, loading, error],
  );

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>;
}

export function useMe(): MeContextValue {
  const ctx = useContext(MeContext);
  if (!ctx) {
    throw new Error('useMe must be used within MeProvider');
  }
  return ctx;
}
